import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, CardText, CardTitle, Col, Input, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Switch from 'react-switch';
import VendorMultiSelect from '../section/VendorMultiSelect';

/**
 * Renders additional fields on Tab 3 (Default Config) based on schema definitions.
 * Each entry in the `extras` array describes a self-contained widget with its own
 * type, data binding, and layout — making it extensible for future field types.
 *
 * Supported widget types:
 *   - "statusVariables": Toggle cards with numeric variable inputs (e.g., PostgreSQL statuses)
 *   - "toggle": Simple labeled toggle switch (e.g., auto-close bug tickets)
 *
 * Each widget definition:
 * {
 *   type: string,         // Widget type
 *   title: string,        // Section heading
 *   subtitle: string,     // Description text below title
 *   stateKey: string,     // Key in props for reading/writing state (e.g., "vendorStatuses")
 *   setterKey: string,    // Key in props for the setter function (e.g., "setVendorStatuses")
 *   condition: object,    // Optional visibility condition
 *   colSize: number,      // Bootstrap col size per item (default 6)
 *   className: string     // Optional CSS class
 * }
 */
const DynamicDefaultConfigExtras = ({ extras, props: parentProps }) => {
  if (!extras || !extras.length) return null;

  return (
    <>
      {extras.map((widget, index) => {
        // Check visibility condition
        if (widget.condition && !evaluateCondition(widget.condition, parentProps)) {
          return null;
        }

        switch (widget.type) {
          case 'statusVariables':
            return <StatusVariablesWidget key={`extra-${index}`} widget={widget} parentProps={parentProps} />;
          case 'toggle':
            return <ToggleWidget key={`extra-${index}`} widget={widget} parentProps={parentProps} />;
          case 'multiSelect':
            return <MultiSelectWidget key={`extra-${index}`} widget={widget} parentProps={parentProps} />;
          default:
            console.warn(`Unknown defaultConfigExtras widget type: ${widget.type}`);
            return null;
        }
      })}
    </>
  );
};

/**
 * StatusVariables widget — renders an array of status objects as toggle cards
 * with numeric variable inputs.
 *
 * Data shape: [{ label, active (0|1), variables: [{ label, value: number }] }]
 *
 * Schema:
 * {
 *   type: "statusVariables",
 *   title: "Select Bug Statuses to Monitor",
 *   subtitle: "Select multiple entries if necessary",
 *   stateKey: "vendorStatuses",
 *   setterKey: "setVendorStatuses",
 *   colSize: 6,
 *   condition: { stateKey: "vendorStatuses", hasProperty: "variables" }
 * }
 */
const StatusVariablesWidget = ({ widget, parentProps }) => {
  const statuses = parentProps[widget.stateKey] || [];
  const setStatuses = parentProps[widget.setterKey];

  if (!statuses.length) return null;

  // Check if statuses have the variables property (condition from schema)
  if (widget.condition?.hasProperty && !statuses[0]?.hasOwnProperty(widget.condition.hasProperty)) {
    return null;
  }

  const handleSwitchToggle = (checked, option) => {
    const updated = [...statuses];
    const idx = statuses.findIndex(item => item.label === option.label);
    updated[idx] = { ...updated[idx], active: checked ? 1 : 0 };
    setStatuses(updated);
  };

  const handleVariableChange = (e, variable, option) => {
    const num = e.target.value;
    const updated = [...statuses];
    const optionIdx = statuses.findIndex(item => item.label === option.label);
    const variables = [...option.variables];
    const varIdx = option.variables.findIndex(item => item.label === variable.label);
    variables[varIdx] = { ...variables[varIdx], value: Number(num) };
    updated[optionIdx] = { ...updated[optionIdx], variables };
    setStatuses(updated);
  };

  const colSize = widget.colSize || 6;

  return (
    <>
      {widget.title && <CardTitle>{widget.title}</CardTitle>}
      {widget.subtitle && <CardText>{widget.subtitle}</CardText>}
      <Row>
        {statuses.map(option => {
          const displayLabel =
            typeof option.label === 'object' ? option.label.label || option.label.value || '' : option.label;
          return (
            <Col lg={String(colSize)} key={displayLabel + 'option'}>
              <Card className="mt-2">
                <CardBody>
                  <Row>
                    <Col xs={8}>
                      <CardTitle>{displayLabel}</CardTitle>
                    </Col>
                    <Col xs={4} className="text-right">
                      <Switch
                        id={displayLabel + '-switch'}
                        onChange={checked => handleSwitchToggle(checked, option)}
                        checked={option.active === 1}
                        uncheckedIcon={false}
                        onColor="#617E71"
                        height={14}
                        width={28}
                      />
                    </Col>
                  </Row>
                  {option.variables &&
                    option.variables.map(variable => (
                      <Row
                        key={variable.label + option.label + 'variableKey'}
                        id={variable.label + option.label + 'variableId'}
                        className="mt-2"
                      >
                        <Col>
                          <strong>
                            {typeof variable.label === 'object'
                              ? variable.label.label || variable.label.value || ''
                              : variable.label}
                          </strong>
                        </Col>
                        <Col>
                          <Input
                            placeholder="0"
                            type="number"
                            value={variable.value}
                            onChange={e => handleVariableChange(e, variable, option)}
                            disabled={option.active !== 1}
                            min={0}
                          />
                        </Col>
                      </Row>
                    ))}
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

/**
 * Toggle widget — renders a labeled toggle switch styled to match the CVE filter.
 *
 * Schema:
 * {
 *   type: "toggle",
 *   label: "Inferred Version Support",
 *   subtitle: "Include bugs whose affected versions were inferred by ODD ...",
 *   helperText: "If enabled, inferred-version bugs are included alongside ...",
 *   tooltip: "Optional tooltip text shown on info-icon hover",
 *   stateKey: "enableInferredVersions",
 *   setterKey: "setEnableInferredVersions"
 * }
 */
const ToggleWidget = ({ widget, parentProps }) => {
  const value = parentProps[widget.stateKey];
  const setValue = parentProps[widget.setterKey];

  if (!setValue) return null;

  const tooltipId = `toggle-tooltip-${widget.stateKey}`;

  return (
    <Row>
      <Col lg="5" className="mb-4">
        <CardTitle>
          {widget.label}
          {widget.tooltip && (
            <>
              {' '}
              <FontAwesomeIcon id={tooltipId} icon="info-circle" style={{ cursor: 'help' }} />
              <UncontrolledTooltip target={tooltipId} placement="right">
                {widget.tooltip}
              </UncontrolledTooltip>
            </>
          )}
        </CardTitle>
        {widget.subtitle && <CardText>{widget.subtitle}</CardText>}
        <Switch
          id={`toggle-switch-${widget.stateKey}`}
          onChange={() => setValue(!value)}
          checked={!!value}
          uncheckedIcon={false}
          checkedIcon={false}
          onColor="#253746"
          height={20}
          width={40}
          handleDiameter={16}
        />
        {widget.helperText && <Label className="ml-2">{widget.helperText}</Label>}
      </Col>
    </Row>
  );
};

/**
 * MultiSelect widget — renders a vendorData-backed multi-select.
 * Options come from `vendor.vendorData[optionsKey]`; helper text from
 * `vendor.vendorData[helperTextKey]` if present (otherwise widget.subtitle).
 * Selection is read/written through dynamicState via setDynamicState.
 *
 * Schema:
 * {
 *   type: "multiSelect",
 *   title: "ServiceNow Auto-Close Statuses",
 *   stateKey: "serviceNowAutoCloseStatuses",
 *   optionsKey: "serviceNowAutoCloseStatuses",
 *   helperTextKey: "serviceNowAutoCloseStatusesHelperText",
 *   feedback: "auto-close statuses",
 *   colSize: 12,
 *   required: false
 * }
 */
const MultiSelectWidget = ({ widget, parentProps }) => {
  const vendorData = parentProps.vendor?.vendorData || {};
  const setDynamicState = parentProps.setDynamicState;
  const isSubmitted = !!parentProps.isSubmitted;

  const rawOptions = vendorData[widget.optionsKey];
  if (!setDynamicState || !Array.isArray(rawOptions) || rawOptions.length === 0) {
    return null;
  }

  const options = normalizeOptionList(rawOptions);
  const selected = normalizeOptionList(parentProps[widget.stateKey]);
  const helperText = (widget.helperTextKey && vendorData[widget.helperTextKey]) || widget.subtitle;
  const colSize = widget.colSize || 12;

  const handleChange = next => {
    setDynamicState(prev => ({ ...prev, [widget.stateKey]: next || [] }));
  };

  return (
    <Row className="mt-2">
      <Col lg={String(colSize)}>
        {widget.title && <CardTitle>{widget.title}</CardTitle>}
        {helperText && <CardText className="text-muted">{helperText}</CardText>}
        <VendorMultiSelect
          options={options}
          setHook={handleChange}
          categories={selected}
          handleSelectChange={(e, setHook) => setHook(e || [])}
          isSubmitted={isSubmitted}
          feedback={widget.feedback || 'an item'}
          name={widget.label || widget.title || widget.stateKey}
          required={!!widget.required}
        />
      </Col>
    </Row>
  );
};

/**
 * Normalizes an option list to [{label, value}] regardless of whether the
 * source is an array of strings or already-shaped objects. Returns [] if
 * the input is missing.
 */
function normalizeOptionList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (item == null) return null;
      if (typeof item === 'string') return { label: item, value: item };
      const value = item.value !== undefined ? item.value : item.label;
      const label = item.label !== undefined ? item.label : item.value;
      if (value === undefined) return null;
      return { ...item, label, value };
    })
    .filter(Boolean);
}

/**
 * Evaluates a visibility condition against parent props.
 * Supports:
 *   { stateKey: "vendorStatuses", hasProperty: "variables" }  — check array[0] has property
 *   { stateKey: "someKey", truthy: true }                     — check value is truthy
 */
function evaluateCondition(condition, props) {
  if (!condition || !condition.stateKey) return true;
  const val = props[condition.stateKey];
  if (condition.hasProperty) {
    return Array.isArray(val) && val.length > 0 && val[0]?.hasOwnProperty(condition.hasProperty);
  }
  if (condition.truthy) {
    return !!val;
  }
  return !!val;
}

StatusVariablesWidget.propTypes = {
  widget: PropTypes.object.isRequired,
  parentProps: PropTypes.object.isRequired
};

ToggleWidget.propTypes = {
  widget: PropTypes.object.isRequired,
  parentProps: PropTypes.object.isRequired
};

MultiSelectWidget.propTypes = {
  widget: PropTypes.object.isRequired,
  parentProps: PropTypes.object.isRequired
};

DynamicDefaultConfigExtras.propTypes = {
  extras: PropTypes.array,
  props: PropTypes.object.isRequired
};

export default DynamicDefaultConfigExtras;
